// Expression evaluation for `calculated` fields. Mirrors the mobile renderer's
// on-device evaluator so the web capture UI (EntityForm) computes the same value
// when a surveyor creates/edits a record straight into the CmTable.
//
// Field references use `$<name>`, where `<name>` resolves to either a field's
// top-level `key` OR its `row_data` column (the `target` suffix) — the builder's
// expression editor lets authors type either, so we accept both.
//
// Function catalog (agreed contract §4.3): IF, COUNTA, CHOICEVALUES, CONCAT,
// LPAD, ISBLANK, plus arithmetic (+ - * /) with precedence + parentheses and the
// comparison operators = == != <> < > <= >=. Evaluation is null-safe: any parse
// or runtime error (missing ref, divide-by-zero, bad regex) yields `null` rather
// than throwing. Calculated fields may reference each other; the cascade in
// `computeCalculatedValues` iterates until stable.
//
// See docs/developer-guides/2026-08-05_form-builder-mobile-contract-alignment.md §4.

import { SchemaField } from "../../types/fieldForm";
import { captureKeyOf } from "./captureKeys";

type Value = string | number | boolean | null | Value[];

// ── Tokenizer ────────────────────────────────────────────────────────────────

interface Token {
  type: "num" | "str" | "ident" | "ref" | "op" | "lparen" | "rparen" | "comma";
  value: string;
}

const MULTI_OPS = ["<=", ">=", "!=", "<>", "=="];
const SINGLE_OPS = new Set(["=", "<", ">", "+", "-", "*", "/"]);

function tokenize(input: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  const isDigit = (c: string) => c >= "0" && c <= "9";
  const isIdentStart = (c: string) => /[A-Za-z_]/.test(c);
  const isIdentPart = (c: string) => /[A-Za-z0-9_]/.test(c);

  while (i < input.length) {
    const c = input[i];
    if (c === " " || c === "\t" || c === "\n" || c === "\r") {
      i++;
      continue;
    }
    if (c === "(") {
      tokens.push({ type: "lparen", value: c });
      i++;
      continue;
    }
    if (c === ")") {
      tokens.push({ type: "rparen", value: c });
      i++;
      continue;
    }
    if (c === ",") {
      tokens.push({ type: "comma", value: c });
      i++;
      continue;
    }
    if (c === '"' || c === "'") {
      const quote = c;
      let s = "";
      i++;
      while (i < input.length && input[i] !== quote) {
        if (input[i] === "\\" && i + 1 < input.length) {
          s += input[i + 1];
          i += 2;
        } else {
          s += input[i];
          i++;
        }
      }
      i++; // closing quote
      tokens.push({ type: "str", value: s });
      continue;
    }
    if (isDigit(c) || (c === "." && isDigit(input[i + 1] ?? ""))) {
      let n = "";
      while (i < input.length && (isDigit(input[i]) || input[i] === ".")) {
        n += input[i];
        i++;
      }
      tokens.push({ type: "num", value: n });
      continue;
    }
    if (c === "$") {
      let name = "";
      i++;
      while (i < input.length && isIdentPart(input[i])) {
        name += input[i];
        i++;
      }
      tokens.push({ type: "ref", value: name });
      continue;
    }
    if (isIdentStart(c)) {
      let name = "";
      while (i < input.length && isIdentPart(input[i])) {
        name += input[i];
        i++;
      }
      tokens.push({ type: "ident", value: name });
      continue;
    }
    const two = input.slice(i, i + 2);
    if (MULTI_OPS.includes(two)) {
      tokens.push({ type: "op", value: two });
      i += 2;
      continue;
    }
    if (SINGLE_OPS.has(c)) {
      tokens.push({ type: "op", value: c });
      i++;
      continue;
    }
    // Unknown character — skip it (null-safe: don't crash on stray input).
    i++;
  }
  return tokens;
}

// ── Parser (recursive descent → AST) ─────────────────────────────────────────

type Node =
  | { kind: "num"; value: number }
  | { kind: "str"; value: string }
  | { kind: "bool"; value: boolean }
  | { kind: "ref"; name: string }
  | { kind: "unary"; op: string; operand: Node }
  | { kind: "binary"; op: string; left: Node; right: Node }
  | { kind: "call"; name: string; args: Node[] };

class Parser {
  private pos = 0;
  constructor(private tokens: Token[]) {}

  private peek(): Token | undefined {
    return this.tokens[this.pos];
  }
  private next(): Token | undefined {
    return this.tokens[this.pos++];
  }

  parse(): Node {
    const node = this.parseComparison();
    return node;
  }

  private parseComparison(): Node {
    let left = this.parseAdditive();
    const t = this.peek();
    if (t?.type === "op" && ["=", "==", "!=", "<>", "<", ">", "<=", ">="].includes(t.value)) {
      this.next();
      const right = this.parseAdditive();
      left = { kind: "binary", op: t.value, left, right };
    }
    return left;
  }

  private parseAdditive(): Node {
    let left = this.parseMultiplicative();
    for (let t = this.peek(); t?.type === "op" && (t.value === "+" || t.value === "-"); t = this.peek()) {
      this.next();
      left = { kind: "binary", op: t.value, left, right: this.parseMultiplicative() };
    }
    return left;
  }

  private parseMultiplicative(): Node {
    let left = this.parseUnary();
    for (let t = this.peek(); t?.type === "op" && (t.value === "*" || t.value === "/"); t = this.peek()) {
      this.next();
      left = { kind: "binary", op: t.value, left, right: this.parseUnary() };
    }
    return left;
  }

  private parseUnary(): Node {
    const t = this.peek();
    if (t?.type === "op" && (t.value === "-" || t.value === "+")) {
      this.next();
      return { kind: "unary", op: t.value, operand: this.parseUnary() };
    }
    return this.parsePrimary();
  }

  private parsePrimary(): Node {
    const t = this.next();
    if (!t) throw new Error("Unexpected end of expression");
    if (t.type === "num") return { kind: "num", value: Number(t.value) };
    if (t.type === "str") return { kind: "str", value: t.value };
    if (t.type === "ref") return { kind: "ref", name: t.value };
    if (t.type === "lparen") {
      const inner = this.parseComparison();
      const close = this.next();
      if (!close || close.type !== "rparen") throw new Error("Missing )");
      return inner;
    }
    if (t.type === "ident") {
      const upper = t.value.toUpperCase();
      if (upper === "TRUE") return { kind: "bool", value: true };
      if (upper === "FALSE") return { kind: "bool", value: false };
      // Function call.
      const open = this.peek();
      if (open?.type === "lparen") {
        this.next();
        const args: Node[] = [];
        if (this.peek()?.type !== "rparen") {
          args.push(this.parseComparison());
          while (this.peek()?.type === "comma") {
            this.next();
            args.push(this.parseComparison());
          }
        }
        const close = this.next();
        if (!close || close.type !== "rparen") throw new Error("Missing ) in call");
        return { kind: "call", name: upper, args };
      }
      // Bare identifier — treat as a string literal (lenient).
      return { kind: "str", value: t.value };
    }
    throw new Error(`Unexpected token: ${t.value}`);
  }
}

// ── Value helpers ────────────────────────────────────────────────────────────

function toNum(v: Value): number {
  if (typeof v === "number") return v;
  if (typeof v === "boolean") return v ? 1 : 0;
  if (typeof v === "string") {
    const n = parseFloat(v);
    return Number.isNaN(n) ? NaN : n;
  }
  return NaN;
}

function toStr(v: Value): string {
  if (v == null) return "";
  if (Array.isArray(v)) return v.map(toStr).join(", ");
  if (typeof v === "boolean") return v ? "true" : "false";
  return String(v);
}

function toBool(v: Value): boolean {
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0;
  const s = String(v ?? "")
    .trim()
    .toLowerCase();
  return s === "true" || s === "yes" || s === "1";
}

function isEmpty(v: Value): boolean {
  if (v == null) return true;
  if (Array.isArray(v)) return v.length === 0;
  return String(v).trim() === "";
}

/** Truthiness for IF conditions. */
function truthy(v: Value): boolean {
  if (v == null) return false;
  if (typeof v === "boolean") return v;
  if (typeof v === "number") return v !== 0 && !Number.isNaN(v);
  if (Array.isArray(v)) return v.length > 0;
  const s = String(v).trim().toLowerCase();
  if (s === "" || s === "false" || s === "no" || s === "0") return false;
  return true;
}

/** Loose equality tolerating boolean ⇄ yes/no/true and number ⇄ string. */
function looseEqual(a: Value, b: Value): boolean {
  if (typeof a === "boolean" || typeof b === "boolean") return toBool(a) === toBool(b);
  const na = toNum(a);
  const nb = toNum(b);
  if (!Number.isNaN(na) && !Number.isNaN(nb)) return na === nb;
  return toStr(a) === toStr(b);
}

/** Flatten one level of array args and coerce to a list of values. */
function asList(v: Value): Value[] {
  if (Array.isArray(v)) return v;
  if (v == null || v === "") return [];
  if (typeof v === "string" && v.includes(",")) return v.split(",").map((s) => s.trim());
  return [v];
}

// ── Evaluator ────────────────────────────────────────────────────────────────

function evalNode(node: Node, resolve: (name: string) => Value): Value {
  switch (node.kind) {
    case "num":
      return node.value;
    case "str":
      return node.value;
    case "bool":
      return node.value;
    case "ref":
      return resolve(node.name);
    case "unary": {
      const v = toNum(evalNode(node.operand, resolve));
      if (Number.isNaN(v)) return null;
      return node.op === "-" ? -v : v;
    }
    case "binary":
      return evalBinary(node, resolve);
    case "call":
      return evalCall(node, resolve);
    default:
      return null;
  }
}

function evalBinary(node: Extract<Node, { kind: "binary" }>, resolve: (name: string) => Value): Value {
  const op = node.op;
  if (op === "=" || op === "==") return looseEqual(evalNode(node.left, resolve), evalNode(node.right, resolve));
  if (op === "!=" || op === "<>") return !looseEqual(evalNode(node.left, resolve), evalNode(node.right, resolve));
  if (op === "<" || op === ">" || op === "<=" || op === ">=") {
    const a = toNum(evalNode(node.left, resolve));
    const b = toNum(evalNode(node.right, resolve));
    if (Number.isNaN(a) || Number.isNaN(b)) return false;
    if (op === "<") return a < b;
    if (op === ">") return a > b;
    if (op === "<=") return a <= b;
    return a >= b;
  }
  // Arithmetic.
  const a = toNum(evalNode(node.left, resolve));
  const b = toNum(evalNode(node.right, resolve));
  if (Number.isNaN(a) || Number.isNaN(b)) return null;
  switch (op) {
    case "+":
      return a + b;
    case "-":
      return a - b;
    case "*":
      return a * b;
    case "/":
      return b === 0 ? null : a / b;
    default:
      return null;
  }
}

function evalCall(node: Extract<Node, { kind: "call" }>, resolve: (name: string) => Value): Value {
  const args = node.args;
  switch (node.name) {
    case "IF": {
      const cond = truthy(evalNode(args[0], resolve));
      if (cond) return args[1] ? evalNode(args[1], resolve) : null;
      return args[2] ? evalNode(args[2], resolve) : null;
    }
    case "COUNTA": {
      let count = 0;
      for (const a of args) {
        for (const item of asList(evalNode(a, resolve))) {
          if (!isEmpty(item)) count++;
        }
      }
      return count;
    }
    case "CHOICEVALUES":
      return args.length ? asList(evalNode(args[0], resolve)) : [];
    case "CONCAT":
      return args.map((a) => toStr(evalNode(a, resolve))).join("");
    case "LPAD": {
      const text = toStr(evalNode(args[0], resolve));
      const length = Math.trunc(toNum(evalNode(args[1], resolve)));
      const padRaw = args[2] ? toStr(evalNode(args[2], resolve)) : "0";
      const pad = padRaw || "0";
      if (Number.isNaN(length) || length <= text.length) return text;
      let out = text;
      while (out.length < length) out = pad + out;
      return out.slice(out.length - length);
    }
    case "ISBLANK":
      return args.length ? isEmpty(evalNode(args[0], resolve)) : true;
    default:
      return null;
  }
}

/**
 * Evaluate a calculation `expression`, resolving `$<name>` via `resolve`. Returns
 * `null` on any parse/eval error (null-safe).
 */
export function evaluateExpression(expression: string, resolve: (name: string) => unknown): Value {
  if (!expression || !expression.trim()) return null;
  try {
    const ast = new Parser(tokenize(expression)).parse();
    return evalNode(ast, (name) => (resolve(name) ?? null) as Value);
  } catch {
    return null;
  }
}

function valuesEqual(a: unknown, b: unknown): boolean {
  if (Array.isArray(a) || Array.isArray(b)) return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
  return String(a ?? "") === String(b ?? "");
}

/**
 * Compute the value of every `calculated` field, keyed by its capture slot (a
 * `row_data` column, or `$name` / `$description` when the field targets one of
 * the row's own columns). Resolves `$<name>` against the captured `values` (by
 * field `key` or column) and against other calculated fields, iterating until
 * stable so chained calculations (A ← B ← C) settle.
 */
export function computeCalculatedValues(fields: SchemaField[], values: Record<string, unknown>): Record<string, Value> {
  const calc = fields.filter((f) => f.widget === "calculated" && captureKeyOf(f.target) != null);
  if (calc.length === 0) return {};

  const byKey = new Map(fields.map((f) => [f.key, f]));
  const computed: Record<string, Value> = {};

  const resolve = (name: string): Value => {
    if (name in computed) return computed[name];
    const f = byKey.get(name);
    if (f) {
      const k = captureKeyOf(f.target);
      if (k && k in computed) return computed[k];
      if (k) return (values[k] ?? null) as Value;
    }
    if (name in values) return (values[name] ?? null) as Value;
    return null;
  };

  const maxPasses = calc.length + 2;
  for (let pass = 0; pass < maxPasses; pass++) {
    let changed = false;
    for (const f of calc) {
      const key = captureKeyOf(f.target) as string;
      const next = evaluateExpression(f.expression ?? "", resolve);
      if (!valuesEqual(computed[key], next)) {
        computed[key] = next;
        changed = true;
      }
    }
    if (!changed) break;
  }
  return computed;
}
